import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, CalendarClock, Receipt, ShieldCheck, Undo2, XCircle, FileText, Download, ClipboardList, Globe, Mail } from "lucide-react";
import { API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { api, formatApiError } from "@/lib/api";
import AuthModal from "@/components/AuthModal";
import ContentCalendarPreview from "@/components/ContentCalendarPreview";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—");

function EmailRow({ user, onSaved }) {
  const [editing, setEditing] = useState(!user.email);
  const [email, setEmail] = useState(user.email || "");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/account/email", { email });
      onSaved(data.user);
      setEditing(false);
      toast.success("Email saved — receipts and password resets go there now.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  if (!editing) {
    return (
      <p className="text-sm text-[#8C7562] mt-2 flex flex-wrap items-center gap-2" data-testid="account-email">
        <Mail size={13} /> {user.email}
        <button onClick={() => setEditing(true)} className="text-xs font-semibold text-[#A8785A] hover:underline" data-testid="account-email-edit-btn">Change</button>
      </p>
    );
  }
  return (
    <form onSubmit={save} className={`mt-4 rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${user.email ? "border-[#E8DCCB] bg-[#FFFDF7]" : "border-[#C48A9F] bg-[#FFF3F8]"}`} data-testid="account-email-form">
      <div className="flex-1">
        {!user.email && <p className="text-xs font-semibold text-[#3A2E25] mb-2">Add an email so you can reset your password and receive receipts.</p>}
        <input className="field !py-2.5" type="email" required placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="account-email-input" />
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="btn-primary !py-2.5 !px-5" data-testid="account-email-save-btn">{busy ? "Saving…" : "Save email"}</button>
        {user.email && <button type="button" onClick={() => { setEditing(false); setEmail(user.email); }} className="btn-ghost !py-2.5 !px-4">Cancel</button>}
      </div>
    </form>
  );
}

function PlanCard({ plan, onChanged }) {
  const [busy, setBusy] = useState(false);
  const billing = plan.billing || {};
  const cancelling = plan.status === "cancelling" || billing.cancel_at_period_end;
  const cancelled = plan.status === "cancelled";
  const act = async (path, msg) => {
    setBusy(true);
    try {
      const { data } = await api.post(path);
      toast.success(msg);
      onChanged(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="glass rounded-2xl p-8" data-testid="account-plan-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="eyebrow">Social media plan</p>
          <h2 className="f-display text-3xl mt-2" data-testid="account-plan-name">{plan.name}</h2>
          <p className="text-sm text-[#8C7562] mt-1">${plan.price}/month · month-to-month</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] ${
          cancelled ? "border-[#E8DCCB] text-[#B9A896]" : cancelling ? "border-[#C48A9F] text-[#C48A9F]" : "border-[#A8785A] text-[#A8785A]"}`} data-testid="account-plan-status">
          <BadgeCheck size={12} /> {cancelled ? "Ended" : cancelling ? "Ends at period end" : "Active"}
        </span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-7">
        <div className="rounded-xl bg-[#FFF3F8] border border-[#E8DCCB] p-4">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C7562] flex items-center gap-1.5"><CalendarClock size={12} /> {cancelling ? "Access until" : "Next billing date"}</p>
          <p className="f-display text-xl mt-1.5" data-testid="account-next-billing">{fmtDate(billing.current_period_end)}</p>
        </div>
        <div className="rounded-xl bg-[#FFFDF7] border border-[#E8DCCB] p-4">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C7562] flex items-center gap-1.5"><ShieldCheck size={12} /> Started</p>
          <p className="f-display text-xl mt-1.5">{fmtDate(plan.started_at)}</p>
        </div>
      </div>
      {!cancelled && (
        <div className="flex flex-wrap items-center gap-3 mt-7 pt-6 border-t border-[#E8DCCB]">
          {cancelling ? (
            <button onClick={() => act("/account/plan/resume", "Your plan will continue — welcome back.")} disabled={busy} className="btn-primary !py-2.5 !px-5" data-testid="account-resume-btn">
              <Undo2 size={13} /> Keep my plan
            </button>
          ) : (
            <button onClick={() => { if (window.confirm("Cancel at the end of this billing period? You keep full service until then.")) act("/account/plan/cancel", "Cancellation scheduled — service continues until your period ends."); }}
              disabled={busy} className="btn-ghost !py-2.5 !px-5 hover:!border-[#C48A9F]" data-testid="account-cancel-btn">
              <XCircle size={13} /> Cancel plan
            </button>
          )}
          <p className="text-[11px] text-[#8C7562]">{cancelling ? "No further charges. You can resume any time before the period ends." : "Cancel anytime — no contracts, no fees. Service runs through the end of the paid period."}</p>
        </div>
      )}
    </div>
  );
}

export default function AccountPage() {
  const { user, ready, refreshUser, setUser } = useAuth();
  const { pages } = useSite();
  const [data, setData] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);

  const load = () => api.get("/account").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { if (user) load(); else setData(null); }, [user]);

  if (!ready) return <div className="min-h-screen bg-[#FAF6EF]" />;
  if (!user) {
    return (
      <main className="min-h-screen vector-grid flex items-center justify-center px-6 pt-16" data-testid="account-login-gate">
        <div className="glass rounded-2xl max-w-md w-full p-10 text-center">
          <span className="eyebrow">Your account</span>
          <h1 className="f-display text-3xl mt-3 mb-3">Log in to see your plan</h1>
          <p className="text-sm text-[#8C7562] mb-8">Your Organizer access, social media plan and receipts live here.</p>
          <button onClick={() => setAuthOpen(true)} className="btn-primary justify-center w-full" data-testid="account-login-btn">Log In</button>
        </div>
        <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      </main>
    );
  }

  const onPlanChanged = async (res) => {
    await refreshUser();
    setData((d) => ({ ...d, social_plan: { ...d.social_plan, status: res.status, billing: res.billing } }));
  };

  return (
    <main className="min-h-screen vector-grid pt-28 pb-24" data-testid="account-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <span className="eyebrow">Account</span>
          <h1 className="f-display text-3xl sm:text-4xl mt-3">Hello, {user.username}.</h1>
          <EmailRow user={user} onSaved={(u) => { setUser(u); load(); }} />
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
          <div className="flex flex-col gap-6">
            {(data?.social_plan || data?.website_orders?.length > 0) && (
              <div className={`rounded-2xl p-6 border ${data.onboarding ? "border-[#E8DCCB] bg-[#FFFDF7]" : "border-[#C48A9F] bg-[#FFF3F8]"}`} data-testid="account-onboarding-card">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="eyebrow flex items-center gap-1.5"><ClipboardList size={12} /> Brand assets form</p>
                    <p className="text-sm mt-2">{data.onboarding ? `Submitted ${fmtDate(data.onboarding.submitted_at)} — thank you.` : "Complete this 5-minute form so we can start your project faster."}</p>
                  </div>
                  <Link to="/onboarding" className={`${data.onboarding ? "btn-ghost" : "btn-primary"} !py-2 !px-4`} data-testid="account-onboarding-btn">{data.onboarding ? "Edit form" : "Start form"}</Link>
                </div>
              </div>
            )}
            {data?.website_orders?.length > 0 && (
              <div className="glass rounded-2xl p-7" data-testid="account-website-orders">
                <p className="eyebrow flex items-center gap-1.5"><Globe size={12} /> Website projects</p>
                <ul className="mt-3 flex flex-col divide-y divide-[#E8DCCB]">
                  {data.website_orders.map((o) => (
                    <li key={o.session_id} className="py-3 flex items-center justify-between gap-3 text-sm" data-testid="account-website-order">
                      <div><p className="font-semibold">{o.name} website</p><p className="text-[11px] text-[#8C7562]">Booked {fmtDate(o.ordered_at)} · {o.status}</p></div>
                      <span className="f-display">${Number(o.price).toFixed(0)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data?.social_plan && (
              <div className="glass rounded-2xl p-7" data-testid="account-reports">
                <p className="eyebrow flex items-center gap-1.5"><FileText size={12} /> Monthly reports</p>
                {!data.reports?.length && <p className="text-sm text-[#8C7562] mt-3">Your first report arrives at the end of your first month.</p>}
                <ul className="mt-3 flex flex-col divide-y divide-[#E8DCCB]">
                  {(data.reports || []).map((r) => (
                    <li key={r.id} className="py-3 flex items-center justify-between gap-3 text-sm" data-testid="account-report-row">
                      <div><p className="font-semibold">{r.label}</p><p className="text-[11px] text-[#8C7562]">{fmtDate(r.uploaded_at)} · {(r.size / 1024).toFixed(0)} KB</p></div>
                      <a href={`${API_BASE}/reports/${r.id}/download?auth=${getToken()}`} className="btn-ghost !py-1.5 !px-3.5" data-testid="account-report-download"><Download size={12} /> PDF</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data?.social_plan ? (
              <>
                <PlanCard plan={data.social_plan} onChanged={onPlanChanged} />
                <ContentCalendarPreview plan={data.social_plan.lookup_key} lockPlan compact />
              </>
            ) : (
              <div className="glass rounded-2xl p-8" data-testid="account-no-plan">
                <p className="eyebrow">Social media plan</p>
                <h2 className="f-display text-2xl mt-2">No plan yet</h2>
                <p className="text-sm text-[#8C7562] mt-2 mb-6 max-w-md">Hand your feed to us — designed posts, edited videos, scheduling and monthly reporting from $199/month, month-to-month.</p>
                <Link to={pages.social ? "/social" : "/inquire"} className="btn-primary" data-testid="account-browse-plans-btn">{pages.social ? "See the plans" : "Send an inquiry"}</Link>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-6">
            <div className="glass rounded-2xl p-7" data-testid="account-organizer-card">
              <p className="eyebrow">The Organizer</p>
              {user.paid ? (
                <>
                  <p className="f-display text-2xl mt-2 flex items-center gap-2"><BadgeCheck size={20} className="text-[#A8785A]" /> Lifetime access</p>
                  <p className="text-xs text-[#8C7562] mt-1">{data?.paid_at ? `Unlocked ${fmtDate(data.paid_at)}` : "Unlocked"}</p>
                  <Link to="/organizer" className="btn-ghost !py-2 !px-4 mt-5" data-testid="account-open-organizer-btn">Open workspace</Link>
                </>
              ) : (
                <>
                  <p className="f-display text-2xl mt-2">Not unlocked</p>
                  <p className="text-xs text-[#8C7562] mt-1">One payment of $49, yours for life.</p>
                  <Link to="/organizer" className="btn-primary !py-2 !px-4 mt-5" data-testid="account-unlock-organizer-btn">See what's inside</Link>
                </>
              )}
            </div>
            <div className="glass rounded-2xl p-7" data-testid="account-receipts">
              <p className="eyebrow flex items-center gap-1.5"><Receipt size={12} /> Receipts</p>
              {!data?.purchases?.length && <p className="text-sm text-[#8C7562] mt-3">No payments yet.</p>}
              <ul className="mt-3 flex flex-col divide-y divide-[#E8DCCB]">
                {(data?.purchases || []).map((p) => (
                  <li key={p.session_id} className="py-3 text-sm flex items-start justify-between gap-3" data-testid="account-receipt-row">
                    <div>
                      <p className="font-semibold">{p.plan || "The Organizer"}</p>
                      <p className="text-[11px] text-[#8C7562]">{fmtDate(p.updated_at)} · {p.payment_status}</p>
                    </div>
                    <span className="f-display">${Number(p.amount).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
